<template>
  <div class="profile-animation" ref="container">
    <div class="profile-animation__inner">
      <svg class="profile-animation__line" ref="svg">
        <path
          fill="none"
          stroke="#666"
          stroke-width="2px"
          v-for="(path, index) in paths"
          :key="index"
          :d="path.d"
        />
      </svg>
      <div class="profile-animation__profiles">
        <div
          class="profile-animation__profiles__inner"
          ref="mainProfileContainer"
        >
          <SocialProfile
            class="profile-animation__social-profile"
            color="#4267B2"
          >
            <FacebookLetterFIcon slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
          <SocialProfile
            class="profile-animation__social-profile"
            color="#FF0000"
          >
            <YoutubeIcon color="#FF8989" slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
          <SocialProfile
            class="profile-animation__social-profile"
            color="#333"
            variant="qr"
          >
            <QrCodeIcon slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
          <SocialProfile
            class="profile-animation__social-profile"
            color="#1DA1F2"
          >
            <TwitterIcon slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
          <SocialProfile
            class="profile-animation__social-profile"
            color="#f9f9f9"
            variant="email"
          >
            <EmailIcon slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
          <SocialProfile
            class="profile-animation__social-profile"
            color="#dd2a7b"
          >
            <InstagramIcon slot="icon" />
            <template slot="link"
              >campsite.bio/{{ currentProfileUsername }}</template
            >
          </SocialProfile>
        </div>
      </div>
      <ScaledDevice
        class="profile-animation__device"
        :width="428"
        :height="868"
        ref="preview"
      >
        <DeviceIphone>
          <template v-if="isClient">
            <nuxt-img
              :src="currentProfileImage"
              :sizes="`xs:500px`"
              :quality="85"
              alt="Campsite.bio profile"
              ref="currentImg"
            />
            <nuxt-img
              :src="nextProfileImage"
              :sizes="`xs:500px`"
              :quality="85"
              alt="Campsite.bio profile"
              ref="nextImg"
            />
          </template>
        </DeviceIphone>
      </ScaledDevice>
    </div>
  </div>
</template>

<script>
import anime from 'animejs';
import {
  computed,
  nextTick,
  onMounted,
  ref,
  toRefs,
  useContext,
  watch,
} from '@nuxtjs/composition-api';
import {
  ScaledDevice,
  DeviceIphone,
  InstagramIcon,
  FacebookLetterFIcon,
  TwitterIcon,
  EmailIcon,
  QrCodeIcon,
  YoutubeIcon,
} from '@campsite-bio/component-lib';
import debounce from 'lodash/debounce';
import shuffle from 'lodash/shuffle';
import { useMediaQuery, useResizeObserver } from '@vueuse/core';

import SocialProfile from '@/components/home/social-profile';

const PROFILE_IMAGES = [
  '/campsites/creators/campsite-jamiebareno.png',
  '/campsites/creators/campsite-heyjessica.png',
  '/campsites/non-profit/campsite-sundanceorg.png',
  '/campsites/creators/campsite-weratedogs.png',
  '/campsites/creators/campsite-choicehacking.png',
  '/campsites/business/campsite-shopify.png',
  '/campsites/education/campsite-arizonacals.png',
  '/campsites/creators/campsite-cassierandolph.png',
  '/campsites/business/campsite-dell.png',
  '/campsites/creators/campsite-artisiq.png',
  '/campsites/education/campsite-miadcollege.png',
  '/campsites/creators/campsite-alexokoroji.png',
  '/campsites/creators/campsite-mattevans.png',
  '/campsites/creators/campsite-adionnewilliams.png',
  '/campsites/small-business/campsite-lunasconsulting.png',
];

export default {
  components: {
    ScaledDevice,
    DeviceIphone,
    InstagramIcon,
    FacebookLetterFIcon,
    TwitterIcon,
    EmailIcon,
    QrCodeIcon,
    YoutubeIcon,
    SocialProfile,
  },

  props: {
    line: {
      type: process.server ? Object : HTMLElement,
      default: null,
    },
  },

  setup(props) {
    const { line } = toRefs(props);
    const { $img, isDev } = useContext();
    const mainProfileContainer = ref(null);
    const profilesContainer = ref(null);
    const container = ref(null);
    const containerRect = ref(null);
    const preview = ref(null);
    const svg = ref(null);
    const paths = ref([]);
    const currentProfileImageIndex = ref(0);
    const currentImg = ref(null);
    const nextImg = ref(null);
    const profileTimeout = ref(null);

    const isLargerScreen = useMediaQuery('(min-width: 992px)');

    useResizeObserver(container, (entries) => {
      renderLines();
      containerRect.value = entries[0].contentRect;
    });

    const previewEl = computed(() => {
      return preview.value ? preview.value.$el : null;
    });

    const profileImagesGenerated = PROFILE_IMAGES.map((image) => {
      return isDev
        ? image
        : $img(image, { width: 500, quality: 85, format: 'webp' });
    });

    const profileUsernames = PROFILE_IMAGES.map((image) => {
      return image.split('.')[0];
    });

    const profileImages = computed(() => {
      return shuffle(profileImagesGenerated);
    });

    const currentProfileImage = computed(() => {
      return profileImages.value[currentProfileImageIndex.value];
    });

    const currentProfileUsername = computed(() => {
      return profileUsernames.find((username) => {
        return currentProfileImage.value.indexOf(username) !== -1;
      });
    });

    const nextProfileImage = computed(() => {
      const index =
        currentProfileImageIndex.value + 1 >= profileImages.value.length
          ? 0
          : currentProfileImageIndex.value + 1;
      return profileImages.value[index];
    });

    function getOffset(el, containerOffset = { top: 0, left: 0 }) {
      const rect = el.getBoundingClientRect();

      return {
        top: rect.top + window.pageYOffset - containerOffset.top,
        left: rect.left + window.pageXOffset - containerOffset.left,
        rect,
      };
    }

    function getCenter(el, containerOffset) {
      const { top, left } = getOffset(el, containerOffset);
      const { width, height } = el.getBoundingClientRect();
      // console.log(el, top, left, width, height);

      return {
        top: top + height / 2,
        left: left + width / 2,
      };
    }

    async function renderLines() {
      paths.value = [];
      await nextTick();

      const mainProfiles = Array.from(
        mainProfileContainer.value.querySelectorAll(
          '.profile-animation__social-profile',
        ),
      );

      const containerPosition = getOffset(container.value);
      const targetPosition = getCenter(previewEl.value, containerPosition);
      paths.value = mainProfiles.map((el) => {
        const {
          left: initialStartX,
          top: initialStartY,
          rect,
        } = getOffset(el, containerPosition);

        const { left: endX, top: endY } = targetPosition;

        let startX = isLargerScreen.value
          ? initialStartX + rect.width
          : initialStartX + rect.width / 2;
        let startY =
          initialStartY +
          rect.height / (isLargerScreen.value ? 5 : 1) -
          (isLargerScreen.value ? 0 : 15);

        // https://stackoverflow.com/questions/49274176/how-to-create-a-curved-svg-path-between-two-points
        // M
        var AX = startX;
        var AY = startY;

        // L
        var BX = Math.abs(endX - startX) * 0.05 + startX;
        var BY = startY;

        // C
        var CX = (endX - startX) * 0.66 + startX;
        var CY = startY;
        var DX = (endX - startX) * 0.33 + startX;
        var DY = endY;
        var EX = -Math.abs(endX - startX) * 0.05 + endX;
        var EY = endY;

        // L
        var FX = endX;
        var FY = endY;

        let path = `M${AX},${AY} L${BX},${BY} C${CX},${CY} ${DX},${DY} ${EX},${EY} ${FX},${FY}`;
        return {
          d: path,
        };
      });
    }

    async function nextImage() {
      clearTimeout(profileTimeout.value);

      anime({
        targets: currentImg.value.$el,
        opacity: [1, 0],
      });
      anime({
        targets: nextImg.value.$el,
        opacity: [0, 1],
        complete() {
          currentProfileImageIndex.value =
            currentProfileImageIndex.value + 1 >= profileImages.value.length
              ? 0
              : currentProfileImageIndex.value + 1;
          profileTimeout.value = setTimeout(nextImage, 4000);
        },
      });
    }

    async function animateIn() {
      await nextTick();

      const timeline = anime.timeline({
        easing: 'easeOutCubic',
      });

      const mainProfiles = Array.from(
        mainProfileContainer.value.querySelectorAll(
          '.profile-animation__social-profile',
        ),
      );

      mainProfiles.forEach((el) => {
        el.style.transform = null;
      });

      const { height: profileContainerHeight, width: profileContainerWidth } =
        mainProfileContainer.value.getBoundingClientRect();
      const { height: profileHeight, width: profileWidth } =
        mainProfiles[0].getBoundingClientRect();

      let profilesTranslate;
      let previewTranslate;
      if (isLargerScreen.value) {
        profilesTranslate = {
          translateY(el, i) {
            const spacing =
              (profileContainerHeight - profileHeight) /
              (mainProfiles.length - 1);
            const endTop = i * spacing;
            return [endTop + profileHeight * 0.25, endTop];
          },
        };
        previewTranslate = {
          translateX: ['100%', 0],
        };
      } else {
        profilesTranslate = {
          translateX(el, i) {
            const spacing =
              (profileContainerWidth - profileWidth) /
              (mainProfiles.length - 1);
            const endLeft = i * spacing;
            return [endLeft + profileWidth * 0.25, endLeft];
          },
        };
        previewTranslate = {
          translateY: ['25%', 0],
        };
      }

      if (line.value) {
        timeline.add({
          targets: line.value,
          scaleY: [0, 1],
        });
      }

      timeline
        .add(
          {
            targets: mainProfiles,
            opacity: [0, 1],
            duration: 750,
            ...profilesTranslate,
            delay: anime.stagger(500),
            complete() {
              renderLines();
              // Start profile swapping
              profileTimeout.value = setTimeout(nextImage, 4000);
            },
          },
          0,
        )
        .add({
          targets: svg.value,
          opacity: [0, 1],
        })
        .add(
          {
            targets: previewEl.value,
            opacity: [0, 1],
            ...previewTranslate,
            duration: 1000,
          },
          0,
        );
    }

    if (process.client) {
      const animateInDebounced = debounce(animateIn, 250);
      watch([isLargerScreen, containerRect], () => animateInDebounced());

      onMounted(() => animateInDebounced());
    }

    return {
      mainProfileContainer,
      profilesContainer,
      container,
      preview,
      currentImg,
      nextImg,
      svg,
      paths,
      isLargerScreen,
      currentProfileImage,
      currentProfileUsername,
      nextProfileImage,
      isClient: process.client,
    };
  },
};
</script>

<style lang="scss" scoped>
@keyframes reveal {
  to {
    stroke-dashoffset: 0;
  }
}

.profile-animation {
  position: relative;

  &__inner {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    z-index: 1;
    position: relative;

    @media (min-width: 992px) {
      align-items: flex-end;
      flex-direction: row;
      height: 610px;
    }
  }

  &__line {
    pointer-events: none;
    position: absolute;
    height: 100%;
    opacity: 0;
    width: 100%;

    line {
      opacity: 0;
      animation: dash 1s linear infinite;
    }

    path {
      animation: reveal 7s linear 0s infinite;
      stroke-dasharray: 10, 5;
      stroke-dashoffset: 60;
    }
  }

  &__profiles {
    display: flex;
    width: 100%;
    max-width: 768px;

    @media (min-width: 992px) {
      flex-direction: column;
      width: 200px;
      height: 505px;
      transform: scale(0.85);
    }

    &__inner {
      position: relative;
      flex-grow: 1;
    }

    .profile-animation__social-profile {
      position: absolute;
      top: 0;
      left: 0;
      width: 175px;
      transition: top 200ms;

      @media (min-width: 992px) {
        width: 200px;

        &:hover:not(:last-child) {
          top: -10%;
        }
      }

      &:first-child {
        position: relative;
      }
    }
  }

  &__social-profile {
    opacity: 0;
  }

  &__device {
    width: 250px;
    z-index: 1;

    @media (min-width: 992px) {
      width: 300px;
    }

    &::v-deep {
      img {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;

        &:first-child {
          z-index: 1;
        }
      }
    }
  }
}

.profile-animation__device.profile-animation__device {
  opacity: 0;
}

@keyframes dash {
  to {
    stroke-dashoffset: -12;
  }
}
</style>
